import { useState, useCallback, useRef } from 'react';
import type { ZodType } from 'zod';
import { ZodError } from 'zod';

/**
 * Thin validation hook that extracts per-field error messages from a Zod schema.
 *
 * Returns `validateAll(data)` → boolean. Forms keep their own `useState` strings;
 * the hook is purely for error extraction. This avoids the `dateString` transform
 * issue — server schemas transform `string → Date`, but forms send strings to the API.
 */
export function useFormValidation(schema: ZodType) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const schemaRef = useRef(schema);
  schemaRef.current = schema;

  const validateAll = useCallback((data: Record<string, unknown>): boolean => {
    const result = schemaRef.current.safeParse(data);
    if (result.success) {
      setErrors({});
      return true;
    }

    const newErrors: Record<string, string> = {};
    for (const issue of (result.error as ZodError).issues) {
      const path = issue.path.length === 0 ? '_root' : issue.path.join('.');
      // First error per path wins
      if (!newErrors[path]) {
        newErrors[path] = issue.message;
      }
    }
    setErrors(newErrors);
    return false;
  }, []);

  const clearFieldError = useCallback((field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  /**
   * Validate a single field on blur. Uses `safeParse({ [name]: value, ...context })` against
   * the schema and filters issues to this field's path — works with any ZodType
   * (not just ZodObject), since we don't rely on `.shape`.
   *
   * Pass `context` for cross-field constraints (e.g. pass `{ password }` when
   * validating `confirmPassword` so the match refine gets both values).
   *
   * Side effect: since partial data will report other fields as "missing", we
   * ONLY surface errors matching the target path. Other fields stay untouched.
   */
  const validateField = useCallback(
    (name: string, value: unknown, context?: Record<string, unknown>): string | null => {
      const result = schemaRef.current.safeParse({ ...context, [name]: value });
      if (result.success) {
        setErrors((prev) => {
          if (!prev[name]) return prev;
          const next = { ...prev };
          delete next[name];
          return next;
        });
        return null;
      }
      const fieldIssue = (result.error as ZodError).issues.find(
        (i) => i.path.length > 0 && i.path[0] === name
      );
      if (fieldIssue) {
        setErrors((prev) => ({ ...prev, [name]: fieldIssue.message }));
        return fieldIssue.message;
      }
      // The only issues are for other fields (missing required) — field is valid
      return null;
    },
    []
  );

  const hasErrors = Object.keys(errors).length > 0;

  return { errors, validateAll, validateField, clearFieldError, clearErrors, hasErrors };
}
